import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import { ProsodyAnalysis } from '../../../services/api.service';
import { SpeakerLabelIndex, resolveLabelMeta } from '../utils/voice-analytics.utils';
import { AnalyticsCard } from './meeting-analytics-panel.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type Props = {
  prosodyAnalysis: ProsodyAnalysis | null;
  labelIndex: SpeakerLabelIndex;
};

type ProsodyFlag = { key: string; label: string };

/** Задача B2: карточка «Голосовые маркеры» — бейджи-флаги человеческим языком
 *  (монотонность, усталость, заминки) + маленькие цифры темпа/вариативности тона.
 *  Показывает только спикеров, у которых сработал хотя бы один флаг. */
const ProsodyMarkersCard: React.FC<Props> = ({ prosodyAnalysis, labelIndex }) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!prosodyAnalysis?.available) return [];
    const bySpeaker = prosodyAnalysis.by_speaker || {};
    const fatigueBySpeaker = prosodyAnalysis.fatigue?.by_speaker || {};
    const hesitationsBySpeaker = prosodyAnalysis.hesitations?.by_speaker || {};

    return Object.entries(bySpeaker)
      .map(([label, metrics], i) => {
        const flags: ProsodyFlag[] = [];
        if (metrics.monotone) {
          flags.push({ key: 'monotone', label: t('voice-analytics.prosody-monotone') });
        }
        if (fatigueBySpeaker[label]?.fatigue_detected) {
          flags.push({ key: 'fatigue', label: t('voice-analytics.prosody-fatigue') });
        }
        const hesitation = hesitationsBySpeaker[label];
        if (hesitation && hesitation.count > 0) {
          flags.push({
            key: 'hesitation',
            label: t('voice-analytics.prosody-hesitation', {
              count: hesitation.count,
              avg: hesitation.avg_latency_seconds.toFixed(1),
            }),
          });
        }
        return { meta: resolveLabelMeta(label, labelIndex, i), metrics, flags };
      })
      .filter(row => row.flags.length > 0);
  }, [prosodyAnalysis, labelIndex, t]);

  if (!rows.length) return null;

  return (
    <AnalyticsCard title={t('voice-analytics.prosody-title')} badge="data">
      <div className={cn('voice-prosody-list')}>
        {rows.map(row => (
          <div key={row.meta.label} className={cn('voice-prosody-row')}>
            <div className={cn('voice-prosody-head')}>
              <span className={cn('voice-prosody-dot')} style={{ backgroundColor: row.meta.color }} />
              <span className={cn('voice-prosody-name')} title={row.meta.displayName}>
                {row.meta.shortName}
              </span>
            </div>
            <div className={cn('voice-prosody-flags')}>
              {row.flags.map(flag => (
                <span key={flag.key} className={cn('voice-prosody-flag', { [flag.key]: true })}>
                  {flag.label}
                </span>
              ))}
            </div>
            <div className={cn('voice-prosody-meta')}>
              {typeof row.metrics.tempo_words_per_sec === 'number' && (
                <span>{t('voice-analytics.prosody-tempo', { value: row.metrics.tempo_words_per_sec.toFixed(1) })}</span>
              )}
              {typeof row.metrics.f0_variability === 'number' && (
                <span>{t('voice-analytics.prosody-variability', { value: row.metrics.f0_variability.toFixed(2) })}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </AnalyticsCard>
  );
};

export default ProsodyMarkersCard;
